import { type ChangeEvent } from 'react';
import { Field, type FieldProps } from 'formik';
import FileUpload, { type FileUploadProps } from '@common/components/controls/FileUpload';
import { type OptionalTestId } from '@common/components/controls/TestId';
import { isFileAllowed, isFileSizeValid } from '@common/utils/validation/validate';

function FileUploadField(props: OptionalTestId<Omit<FileUploadProps, 'onDelete' | 'value'>>) {
  const {
    name,
    'data-testid': testId,
    ...rest
  } = props;

  return (
    <Field name={name}>
      {({ field, form, meta: { touched, error } }: FieldProps<FileUploadProps['value']>) => (
        <FileUpload
          {...rest}
          {...field}
          touched={touched}
          error={error}
          data-testid={testId || `${name}-fileupload`} // NOSONAR:S6606 - Empty testId should use default
          onChange={(ev: ChangeEvent<HTMLInputElement>) => {
            const { files } = ev.target as HTMLInputElement;

            if (files) {
              // Validate files before adding them to the form value
              const fileArray = Array.from(files);
              const validFiles: File[] = [];
              const rejectedFiles: Array<{ file: File; reason: string; }> = [];

              fileArray.forEach((file) => {
                // Check file type validation
                if (rest.accept && !isFileAllowed(file, rest.accept)) {
                  rejectedFiles.push({
                    file,
                    reason: `Invalid file type. Accepted types: ${rest.accept}`,
                  });
                  return;
                }

                // Check file size validation
                if (rest.size && !isFileSizeValid(file, rest.size)) {
                  const maxSizeMB = (rest.size / (1024 * 1024)).toFixed(2);
                  rejectedFiles.push({
                    file,
                    reason: `File too large. Maximum size: ${maxSizeMB}MB`,
                  });
                  return;
                }

                validFiles.push(file);
              });

              // Only add valid files to the form
              if (validFiles.length > 0) {
                form.setFieldValue(
                  name,
                  rest.multiple ? [...field.value, ...validFiles] : validFiles,
                );
              }

              // If any files were rejected, set a field error to notify the user
              if (rejectedFiles.length > 0) {
                const rejectedNames = rejectedFiles.map((r) => r.file.name).join(', ');
                form.setFieldError(
                  name,
                  `${rejectedFiles.length} file(s) rejected: ${rejectedNames}. ${
                    rejectedFiles[0].reason
                  }`,
                );
                // Mark field as touched to show the error
                form.setFieldTouched(name, true, false);
              }
            }
          }}
          onDelete={(fileName, secondId) => {
            const newList = field.value.filter((
              value: File,
              index,
            ) => !(value.name === fileName && secondId === index));
            form.setFieldValue(name, newList);
          }}
        />
      )}
    </Field>
  );
}

export default FileUploadField;
